import gsap from "gsap"
import { module as BaseModule } from "modujs"
import Swiper from "swiper"
import {
  Autoplay,
  EffectFade,
  Keyboard,
  Navigation,
  Pagination,
  Thumbs,
} from "swiper/modules"

import "swiper/css"
import "swiper/css/effect-fade"
import "swiper/css/keyboard"
import "swiper/css/navigation"
import "swiper/css/pagination"
import "swiper/css/thumbs"

export default class extends BaseModule {
  init() {
    const slider = this.el
    const { sliderConfig } = slider.dataset

    let thumbsSwiper
    if (sliderConfig === "modal") {
      const modalEl = slider.closest("[data-module-modal]")
      const thumbsSwiperEl = modalEl.querySelector(".thumbs-swiper")

      if (!thumbsSwiperEl) {
        console.error("Thumbs swiper element not found")
        return
      }

      thumbsSwiper = new Swiper(thumbsSwiperEl, {
        loop: true,
        draggable: true,
        slidesPerView: "auto",
        spaceBetween: 8,
        freeMode: true,
        watchSlidesProgress: true,
        breakpoints: {
          375: {
            spaceBetween: 12,
          },
        },
      })
    }

    const params = {
      business: {
        modules: [Keyboard, Autoplay],
        loop: true,
        draggable: true,
        slidesPerView: "auto",
        spaceBetween: 24,
        breakpoints: {
          1024: {
            spaceBetween: 64,
          },
        },
        grabCursor: true,
        centeredSlides: true,
        keyboard: {
          enabled: true,
          onlyInViewport: false,
        },
        autoplay: {
          delay: 5000,
          disableOnInteraction: false,
        },
        on: {
          init: (instance) => {
            const next = document.querySelectorAll("[data-slider-next]")
            const prev = document.querySelectorAll("[data-slider-prev]")

            next.forEach((btn) => {
              btn.addEventListener("click", () => {
                instance.slideNext()
              })
            })
            prev.forEach((btn) => {
              btn.addEventListener("click", () => {
                instance.slidePrev()
              })
            })
          },
        },
      },
      modal: {
        modules: [Navigation, Pagination, EffectFade, Keyboard, Thumbs],
        thumbs: {
          swiper: thumbsSwiper,
        },
        loop: true,
        draggable: false,
        allowTouchMove: false,
        slidesPerView: 1,
        spaceBetween: 0,
        grabCursor: false,
        effect: "fade",
        fadeEffect: {
          crossFade: true,
        },
        keyboard: {
          enabled: true,
          onlyInViewport: false,
        },
        navigation: {
          nextEl: slider.querySelector("[data-slider-next]"),
          prevEl: slider.querySelector("[data-slider-prev]"),
        },
        on: {
          slideChange: (instance) => {
            const current = slider.querySelector("[data-slider-current]")
            if (instance.realIndex !== undefined)
              current.innerHTML = String(instance.realIndex + 1).padStart(
                2,
                "0"
              )
            
            // Reset zoom when slide changes
            const currentSlide = instance.slides[instance.activeIndex]
            if (currentSlide) {
              const image = currentSlide.querySelector("img")
              if (image) {
                instance.zoomScale = 1
                gsap.to(image, {
                  scale: 1,
                  duration: 0.3,
                  ease: "power2.out",
                })
              }
            }
          },
          init: (instance) => {
            // Initialize zoom scale
            instance.zoomScale = 1
            const sliderEl = instance.el
            const handleSlideClick = (event) => {
              if (event.defaultPrevented) return

              const target = event.target
              if (!target) return

              const isControl = target.closest(
                "[data-slider-prev], [data-slider-next], [data-slider-zoom-in], [data-slider-zoom-out], .thumbs-swiper, button, a"
              )
              if (isControl) return

              instance.slideNext()
            }

            sliderEl.addEventListener("click", handleSlideClick)
            
            // Get zoom buttons
            const zoomInBtn = slider.querySelector("[data-slider-zoom-in]")
            const zoomOutBtn = slider.querySelector("[data-slider-zoom-out]")
            
            // Helper function to get current slide image
            const getCurrentImage = () => {
              const currentSlide = instance.slides[instance.activeIndex]
              return currentSlide ? currentSlide.querySelector("img") : null
            }
            
            // Helper function to apply zoom
            const applyZoom = (scale) => {
              const image = getCurrentImage()
              if (image) {
                instance.zoomScale = Math.max(1, Math.min(scale, 5)) // Clamp between 1 and 5
                gsap.to(image, {
                  scale: instance.zoomScale,
                  duration: 0.3,
                  ease: "power2.out",
                })
              }
            }
            
            // Zoom in handler
            if (zoomInBtn) {
              zoomInBtn.addEventListener("click", () => {
                applyZoom(instance.zoomScale + 0.5)
              })
            }
            
            // Zoom out handler
            if (zoomOutBtn) {
              zoomOutBtn.addEventListener("click", () => {
                applyZoom(instance.zoomScale - 0.5)
              })
            }
            
            document.addEventListener("modal:closed", ({ detail }) => {

              const modal = detail.modal
              const nestedModalSlider = modal.querySelector(".swiper")
              const sliderElement = instance.el

              if (nestedModalSlider === sliderElement) {
                setTimeout(() => {
                  instance.slideTo(0)
                  // Reset zoom when modal closes
                  instance.zoomScale = 1
                  const firstSlide = instance.slides[0]
                  if (firstSlide) {
                    const image = firstSlide.querySelector("img")
                    if (image) {
                      gsap.set(image, { scale: 1 })
                    }
                  }
                }, 250)
              }
            })
          },
        },
      },
      cover: {
        modules: [Navigation, Autoplay, Pagination, Keyboard, EffectFade],
        loop: true,
        draggable: true,
        slidesPerView: 1,
        speed: 700,
        spaceBetween: 0,
        grabCursor: true,
        effect: "fade",
        fadeEffect: {
          crossFade: true,
        },
        keyboard: {
          enabled: true,
          onlyInViewport: false,
        },
        autoplay: {
          delay: 5000,
          disableOnInteraction: false,
        },
        pagination: {
          el: slider.querySelector("[data-slider-pagination]"),
          clickable: true,
        },
        on: {
          init: (instance) => {
            const activeSlide = instance.slides[instance.activeIndex]
            instance.slides.forEach((slide) => {
              const image = slide.querySelector("img")
              if (!image) return
              const isActive = slide === activeSlide
              gsap.set(image, { scale: isActive ? 1.1 : 1 })
            })
          },
          slideChangeTransitionStart: (instance) => {
            const previousSlide = instance.slides[instance.previousIndex]
            if (previousSlide) {
              const image = previousSlide.querySelector("img")
              if (image) {
                gsap.to(image, {
                  scale: 1,
                  duration: 0.3,
                  ease: "power2.out",
                  overwrite: true,
                })
              }
            }
          },
          autoplayTimeLeft(s, time, progress) {
            const timescale = 1 - progress
            const { bullets } = s.pagination

            bullets.forEach((bullet, index) => {
              if (index !== s.realIndex) {
                bullet.style.setProperty("--timescale", 0)
              }
              if (index === s.realIndex) {
                bullet.style.setProperty("--timescale", timescale)
              }
            })

            const activeSlide = s.slides[s.activeIndex]
            if (activeSlide) {
              const image = activeSlide.querySelector("img")
              if (image) {
                const scale = 1.05 - timescale * 0.05
                gsap.to(image, {
                  scale,
                  duration: 0.3,
                  ease: "power1.out",
                  overwrite: true,
                })
              }
            }
          },
        },
      },
    }

    this.swiper = new Swiper(slider, params[sliderConfig])
    gsap.to(slider, {
      opacity: 1,
      duration: 3,
      ease: "power2.Out",
    })
  }
}
